/**
 * The reusable halves of `/multiplayer`'s run-card and create-form wiring
 * (multiplayer-entry-and-notifications design § Structure) — split out of `multiplayer-page.js` so
 * the standalone page and the start-screen surfaces (the section's fetched tables panel, PR 1; the
 * section's create-row overlay, PR 2) share one implementation each instead of copies drifting.
 *
 * `enhanceTables(root, {onEnter})` — root-scoped queries (`root.querySelectorAll`, never
 * `document.querySelector`/id selectors), so two mounted instances (the page's own runs section
 * and the section's fetched panel — in principle even concurrently) never cross-talk. Enter
 * behavior is the injected `onEnter` callback (round-1 spec finding): the two consumers enter
 * differently by design — the standalone page persists the handle and navigates (`wirePage`'s own
 * `enterGame`), the section calls `enterRun` for an in-page deal (`game-start-session.js`).
 *
 * `wireCreateForm(root, {onCreated})` — the sibling extraction (PR 2's create overlay): every bit
 * of mutable selection state (`invitees`, `gameMode`) lives in this call's own closure, not a
 * module-level `let` — load-bearing, since the page and the section's overlay can both have an
 * instance mounted (the overlay opens *from* the section, which sits on the same screen), and a
 * shared module-level `let` would let one instance's clicks silently mutate the other's selection.
 * `onCreated(gameId)` is the injected post-create behavior, the same shape as `enhanceTables`'s
 * `onEnter` — the page persists-and-navigates, the overlay dismisses-and-`enterRun`s.
 *
 * **Never imports `game-start/` or `api.js`** — the standalone page's import graph must stay
 * exactly as lean as it is today; only each caller's injected callback ever reaches into
 * game-start territory, and that happens at the call site, not here.
 */
import { createChalice, createFallenChalice } from "./icons/game-type-icons.js";
import { track } from "./analytics.js";

// The mode emblem on the run cards — moved here verbatim from multiplayer-page.js, the wiring's
// former home. The slate is this surface's own fixed ink, NOT the start screen's mode-card
// colour: a mode card's chalice is rewritten to `currentColor` on build (game-start-ui.js's
// makeRecolorable) and takes `--text-secondary` from `.mode-icon`, so the two never share a
// rendered value to keep in step.
const EMBLEM_SIZE = 40; // play-test: fills the run-card row height without dominating the names
const EMBLEM_COLOR = "#94A3B8";

const CSRF_HEADERS = { "X-Requested-With": "XMLHttpRequest" };
const MAX_INVITEES = 2;

/**
 * Mounts the mode emblem into every `.mp-run-emblem` slot and wires every `.mp-run` card's click
 * to `onEnter(gameId)`, both scoped to `root` — safe to call over a detached fragment or a
 * mounted panel without touching anything outside it.
 */
export function enhanceTables(root, { onEnter }) {
  for (const card of root.querySelectorAll(".mp-run")) {
    card.addEventListener("click", () => onEnter(card.dataset.gameId));
  }
  for (const slot of root.querySelectorAll(".mp-run-emblem")) {
    slot.appendChild(
      slot.dataset.mode === "RAMSCH"
        ? createFallenChalice(EMBLEM_SIZE, EMBLEM_COLOR)
        : createChalice(EMBLEM_SIZE, EMBLEM_COLOR),
    );
  }
}

function seatsNote(root) {
  return root.querySelector("#mp-seats-note");
}

function refreshCreateState(root, invitees) {
  const cta = root.querySelector("#mp-create-cta");
  cta.disabled = invitees.length === 0;
  const note = seatsNote(root);
  if (invitees.length === 0) {
    note.textContent = note.dataset.msgPick;
  } else if (invitees.length === 1) {
    note.textContent = note.dataset.msgLothar;
  } else {
    note.textContent = note.dataset.msgFull;
  }
  // Directory chips mirror the selection; a full table disables the unselected rest.
  for (const chip of root.querySelectorAll(".mp-player")) {
    const selected = invitees.includes(chip.dataset.name);
    chip.setAttribute("aria-pressed", String(selected));
    chip.disabled = !selected && invitees.length >= MAX_INVITEES;
  }
}

function wireModeRow(root, setGameMode) {
  const buttons = root.querySelectorAll(".mp-mode-btn");
  for (const btn of buttons) {
    btn.addEventListener("click", () => {
      setGameMode(btn.dataset.mode);
      for (const other of buttons) {
        other.setAttribute("aria-pressed", String(other === btn));
      }
    });
  }
}

function wireDirectory(root, invitees, toggleInvitee) {
  for (const chip of root.querySelectorAll(".mp-player")) {
    chip.addEventListener("click", () => toggleInvitee(chip.dataset.name));
  }
  const search = root.querySelector("#mp-search");
  search.addEventListener("input", () => {
    const needle = search.value.trim().toLowerCase();
    for (const chip of root.querySelectorAll(".mp-player")) {
      const match =
        needle === "" || chip.dataset.name.toLowerCase().includes(needle);
      chip.hidden = !match;
    }
  });
  // Enter adds the typed name as-is — the settled directory split: the browsable list shows
  // board-approved names only, but any exact registered name resolves (the server validates on
  // create; an unknown name 400s into the error line below).
  search.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const name = search.value.trim();
    if (name !== "" && !invitees().includes(name)) {
      toggleInvitee(name);
      search.value = "";
      search.dispatchEvent(new Event("input"));
    }
  });
}

// `state` is a plain mutable object ({invitees, gameMode}) shared by reference across the sibling
// helpers below — the closure-per-call-site shape (file header) with each function kept under the
// size budget: a `let`-closing nested function inlines its own body into wireCreateForm's own
// count (that's what pushed it red before this split), where a shared object passed to top-level
// functions doesn't.
function toggleInvitee(root, state, name) {
  if (state.invitees.includes(name)) {
    state.invitees = state.invitees.filter((n) => n !== name);
  } else if (state.invitees.length < MAX_INVITEES) {
    state.invitees = [...state.invitees, name];
  }
  refreshCreateState(root, state.invitees);
}

async function createRun(root, state, onCreated) {
  const errorLine = root.querySelector("#mp-create-error");
  errorLine.hidden = true;
  const response = await fetch("/api/multiplayer/runs", {
    method: "POST",
    headers: { ...CSRF_HEADERS, "Content-Type": "application/json" },
    body: JSON.stringify({
      invitees: state.invitees,
      gameMode: state.gameMode,
    }),
  });
  if (response.ok) {
    const body = await response.json();
    track("table-created", {
      mode: state.gameMode.toLowerCase(),
      invitees: String(state.invitees.length),
    });
    onCreated(body.gameId);
    return;
  }
  errorLine.textContent =
    response.status === 409
      ? errorLine.dataset.msgExists
      : errorLine.dataset.msgFailed;
  errorLine.hidden = false;
}

function wireCreate(root, state, onCreated) {
  root.querySelector("#mp-create-cta").addEventListener("click", () => {
    createRun(root, state, onCreated).catch((e) => {
      console.error(
        "[multiplayer-tables] create table failed:",
        e?.message ?? e,
      );
      const errorLine = root.querySelector("#mp-create-error");
      errorLine.textContent = errorLine.dataset.msgFailed;
      errorLine.hidden = false;
    });
  });
}

/**
 * Wires the create-a-table form over `root`: mode pick, 1–2 invitees off the directory chips or
 * an exact typed name on Enter, `POST /api/multiplayer/runs`, the 409/error lines. `invitees`/
 * `gameMode` live in this call's own state object (see file header — load-bearing for two
 * concurrent instances). Fires `table-created` on success (`analytics.js#track`) — the one seam
 * both the page's own form and PR 2's create overlay go through, so the event wires exactly once.
 */
export function wireCreateForm(root, { onCreated }) {
  const state = { invitees: [], gameMode: "SKAT" };
  wireModeRow(root, (mode) => (state.gameMode = mode));
  wireDirectory(
    root,
    () => state.invitees,
    (name) => toggleInvitee(root, state, name),
  );
  wireCreate(root, state, onCreated);
  refreshCreateState(root, state.invitees);
}
